import React, { useState, useEffect } from 'react';
import { Settings, Save, Check, ShieldAlert, ShieldCheck, KeyRound, ExternalLink } from 'lucide-react';
import { ShopSettings } from '../../types';

export const AdminSettings: React.FC = () => {
  const [settings, setSettings] = useState<ShopSettings | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      const res = await fetch('/api/admin/settings');
      if (res.ok) {
        const data = await res.json() as any;
        setSettings(data.settings);
      }
    } catch {}
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const saveKey = async (key: string, value: any) => {
    try {
      const res = await fetch('/api/admin/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value })
      });
      if (res.ok) {
        setMessage('Paramètres enregistrés avec succès.');
        setTimeout(() => setMessage(null), 3000);
      }
    } catch {
      alert('Erreur lors de la sauvegarde.');
    }
  };

  if (!settings) {
    return <div className="p-8 text-center text-gray-500">Chargement des paramètres...</div>;
  }

  return (
    <div className="space-y-8 max-w-4xl text-xs">
      <div>
        <h2 className="text-lg font-bold text-gray-900">Paramètres de la Boutique</h2>
        <p className="text-gray-500">Gérez les statuts de vente, la livraison honnête et les mentions légales</p>
      </div>

      {message && (
        <div className="p-3 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl font-semibold">
          {message}
        </div>
      )}

      {/* Master Toggles: Sales & Payments */}
      <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
        <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-pink-600" />
          <span>Interrupteurs Généraux</span>
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <label className="flex items-center gap-3 p-4 border rounded-xl bg-gray-50/50 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(settings.store_status.sales_enabled)}
              onChange={e => {
                const next = { ...settings.store_status, sales_enabled: e.target.checked ? 1 : 0 };
                setSettings({ ...settings, store_status: next });
                saveKey('store_status', next);
              }}
              className="w-4 h-4 text-pink-600 rounded"
            />
            <div>
              <span className="font-bold block text-gray-900">Activer les Ventes</span>
              <span className="text-gray-500 text-[11px]">Si désactivé, le panier ne permet pas de commander</span>
            </div>
          </label>

          <label className="flex items-center gap-3 p-4 border rounded-xl bg-gray-50/50 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(settings.store_status.payments_enabled)}
              onChange={e => {
                const next = { ...settings.store_status, payments_enabled: e.target.checked ? 1 : 0 };
                setSettings({ ...settings, store_status: next });
                saveKey('store_status', next);
              }}
              className="w-4 h-4 text-pink-600 rounded"
            />
            <div>
              <span className="font-bold block text-gray-900">Activer les Paiements Stripe</span>
              <span className="text-gray-500 text-[11px]">Si désactivé, le checkout Stripe est bloqué fail-closed</span>
            </div>
          </label>
        </div>
      </div>

      {/* Shipping Configuration */}
      <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-bold text-sm text-gray-900">Frais de Port & Livraison</h3>
          <button
            onClick={() => saveKey('shipping_config', settings.shipping_config)}
            className="px-3 py-1.5 bg-neutral-900 text-white rounded-lg font-bold hover:bg-pink-600"
          >
            Enregistrer la livraison
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <div>
            <label className="block font-semibold mb-1">Frais de livraison Suisse (CHF)</label>
            <input
              type="number"
              step="0.10"
              value={settings.shipping_config.shipping_chf}
              onChange={e =>
                setSettings({
                  ...settings,
                  shipping_config: { ...settings.shipping_config, shipping_chf: parseFloat(e.target.value) }
                })
              }
              className="w-full p-2 border rounded-lg"
            />
          </div>

          <div>
            <label className="block font-semibold mb-1">Frais de livraison Europe (EUR)</label>
            <input
              type="number"
              step="0.10"
              value={settings.shipping_config.shipping_eur}
              onChange={e =>
                setSettings({
                  ...settings,
                  shipping_config: { ...settings.shipping_config, shipping_eur: parseFloat(e.target.value) }
                })
              }
              className="w-full p-2 border rounded-lg"
            />
          </div>

          <div className="sm:col-span-2 pt-2 border-t">
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={Boolean(settings.shipping_config.free_shipping_enabled)}
                onChange={e =>
                  setSettings({
                    ...settings,
                    shipping_config: { ...settings.shipping_config, free_shipping_enabled: e.target.checked ? 1 : 0 }
                  })
                }
                className="rounded text-pink-600"
              />
              <span className="font-bold text-gray-900">Activer la livraison offerte à partir d'un seuil</span>
            </label>

            {Boolean(settings.shipping_config.free_shipping_enabled) && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold mb-1">Seuil Suisse (CHF)</label>
                  <input
                    type="number"
                    value={settings.shipping_config.free_shipping_threshold_chf}
                    onChange={e =>
                      setSettings({
                        ...settings,
                        shipping_config: {
                          ...settings.shipping_config,
                          free_shipping_threshold_chf: parseFloat(e.target.value)
                        }
                      })
                    }
                    className="w-full p-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-1">Seuil Europe (EUR)</label>
                  <input
                    type="number"
                    value={settings.shipping_config.free_shipping_threshold_eur}
                    onChange={e =>
                      setSettings({
                        ...settings,
                        shipping_config: {
                          ...settings.shipping_config,
                          free_shipping_threshold_eur: parseFloat(e.target.value)
                        }
                      })
                    }
                    className="w-full p-2 border rounded-lg"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Delivery delay texts */}
          <div className="sm:col-span-2 pt-2 border-t space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(settings.shipping_config.show_delivery_delays)}
                onChange={e =>
                  setSettings({
                    ...settings,
                    shipping_config: { ...settings.shipping_config, show_delivery_delays: e.target.checked ? 1 : 0 }
                  })
                }
                className="rounded text-pink-600"
              />
              <span className="font-bold text-gray-900">Afficher les délais réels sur la fiche produit</span>
            </label>

            <div>
              <label className="block font-semibold mb-1">Délais affichés (FR)</label>
              <input
                type="text"
                value={settings.shipping_config.delivery_delay_fr || ''}
                onChange={e =>
                  setSettings({
                    ...settings,
                    shipping_config: { ...settings.shipping_config, delivery_delay_fr: e.target.value }
                  })
                }
                className="w-full p-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block font-semibold mb-1">Délais affichés (DE)</label>
              <input
                type="text"
                value={settings.shipping_config.delivery_delay_de || ''}
                onChange={e =>
                  setSettings({
                    ...settings,
                    shipping_config: { ...settings.shipping_config, delivery_delay_de: e.target.value }
                  })
                }
                className="w-full p-2 border rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Legal Info */}
      <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-bold text-sm text-gray-900">Coordonnées Légales & Footer</h3>
          <button
            onClick={() => saveKey('legal_info', settings.legal_info)}
            className="px-3 py-1.5 bg-neutral-900 text-white rounded-lg font-bold hover:bg-pink-600"
          >
            Enregistrer le légal
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <div>
            <label className="block font-semibold mb-1">Nom commercial</label>
            <input
              type="text"
              value={settings.legal_info.shop_name}
              onChange={e =>
                setSettings({ ...settings, legal_info: { ...settings.legal_info, shop_name: e.target.value } })
              }
              className="w-full p-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Raison sociale</label>
            <input
              type="text"
              value={settings.legal_info.company_name}
              onChange={e =>
                setSettings({ ...settings, legal_info: { ...settings.legal_info, company_name: e.target.value } })
              }
              className="w-full p-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Adresse</label>
            <input
              type="text"
              value={settings.legal_info.address_line1}
              onChange={e =>
                setSettings({ ...settings, legal_info: { ...settings.legal_info, address_line1: e.target.value } })
              }
              className="w-full p-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Code postal & Ville</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={settings.legal_info.postal_code}
                onChange={e =>
                  setSettings({ ...settings, legal_info: { ...settings.legal_info, postal_code: e.target.value } })
                }
                className="w-24 p-2 border rounded-lg"
              />
              <input
                type="text"
                value={settings.legal_info.city}
                onChange={e =>
                  setSettings({ ...settings, legal_info: { ...settings.legal_info, city: e.target.value } })
                }
                className="flex-1 p-2 border rounded-lg"
              />
            </div>
          </div>
          <div>
            <label className="block font-semibold mb-1">E-mail de contact légal</label>
            <input
              type="email"
              value={settings.legal_info.email}
              onChange={e =>
                setSettings({ ...settings, legal_info: { ...settings.legal_info, email: e.target.value } })
              }
              className="w-full p-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Numéro IDE / TVA</label>
            <input
              type="text"
              value={settings.legal_info.company_id}
              onChange={e =>
                setSettings({ ...settings, legal_info: { ...settings.legal_info, company_id: e.target.value } })
              }
              className="w-full p-2 border rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* Admin Security Card (Cloudflare Secrets) */}
      <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
        <div className="flex items-center gap-2 text-gray-900 font-bold text-sm">
          <ShieldCheck className="w-5 h-5 text-emerald-600" />
          <span>Sécurité & Authentification Administrateur</span>
        </div>

        <div className="p-4 bg-gray-50 border border-gray-200/80 rounded-xl space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-pink-100 text-pink-700 flex items-center justify-center shrink-0 font-bold text-xs">
              CF
            </div>
            <div className="space-y-1">
              <p className="font-semibold text-gray-900 text-xs">
                Authentification maître gérée par Cloudflare Secrets
              </p>
              <p className="text-gray-600 text-xs leading-relaxed">
                Votre mot de passe et vos identifiants d'accès administrateur sont protégés et chiffrés directement au niveau de l'infrastructure Cloudflare via les variables secrètes <code className="px-1.5 py-0.5 bg-gray-200/70 text-gray-800 rounded font-mono text-[11px]">ADMIN_PASSWORD</code> et <code className="px-1.5 py-0.5 bg-gray-200/70 text-gray-800 rounded font-mono text-[11px]">ADMIN_USERNAME</code>.
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200/70 pt-3 text-gray-600 space-y-1.5 text-[11px]">
            <p className="font-semibold text-gray-800">
              Pour mettre à jour votre mot de passe ou identifiant administrateur :
            </p>
            <ol className="list-decimal list-inside space-y-1 pl-1 text-gray-700">
              <li>Rendez-vous sur votre <span className="font-semibold text-gray-900">Cloudflare Dashboard</span>.</li>
              <li>Allez dans <span className="font-semibold text-gray-900">Workers &amp; Pages</span> &gt; <span className="font-semibold text-gray-900">fuckthisshit-mug-shop</span>.</li>
              <li>Cliquez sur <span className="font-semibold text-gray-900">Settings</span> &gt; <span className="font-semibold text-gray-900">Variables and secrets</span>.</li>
              <li>Modifiez la valeur secrète de <code className="font-mono text-pink-600 bg-pink-50 px-1 py-0.5 rounded">ADMIN_PASSWORD</code> (ou <code className="font-mono text-pink-600 bg-pink-50 px-1 py-0.5 rounded">ADMIN_USERNAME</code>).</li>
            </ol>
            <p className="text-gray-500 pt-1 italic">
              Cette méthode garantit le plus haut niveau de sécurité : aucun mot de passe n'est stocké en clair, ni dans le code source Git, ni dans les requêtes client.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
